import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";

import type { ProductDetail, ProductWriteRequest } from "@motolynk/api-client";
import { isApiError } from "@motolynk/api-client";
import {
  applyServerErrorPaths,
  TranslationsField,
  translationErrors,
  useRichTextLabels,
  useRules,
  useSlugField,
} from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import {
  Alert,
  Button,
  FormActions,
  FormField,
  FormSection,
  Input,
  LoadingState,
  NumberInput,
  PatternInput,
  RichTextEditor,
  Select,
  Switch,
  Textarea,
  textRules,
  toast,
} from "@motolynk/ui";

import { FaqsSection } from "./FaqsSection";
import { FitmentSection } from "./FitmentSection";
import { PhotosSection } from "./PhotosSection";
import { SpecsSection } from "./SpecsSection";
import {
  emptyValues,
  flattenCategories,
  toPayload,
  toValues,
  type EditorSource,
  type EditorValues,
} from "./values";
import { VariantsSection } from "./VariantsSection";

const NONE = "__none__";
const CONDITIONS = ["new", "used", "open_box", "refurbished"] as const;
/** The API's limits (backend apps/products/services/editor.py: MAX_DIMENSION, warranty 0-240, slug 220). */
const MAX_DIMENSION = 10_000_000;
const MAX_WARRANTY_MONTHS = 240;
const SLUG_MAX = 220;

export interface ProductEditorProps {
  source: EditorSource;
  /** The product being edited (empty = a new product). */
  initial?: ProductDetail;
  /** Saves the payload; a rejected promise with an API error puts the messages on the fields. */
  onSave: (payload: ProductWriteRequest) => Promise<unknown>;
  saveLabel: string;
  /** More buttons next to Save (e.g. Publish, Delete). */
  actions?: ReactNode;
}

/**
 * The shared product editor (admin: company products and fixes; vendor panel: own products). One form, one save:
 * basic information, specifications, variants with price and stock, photos, compatibility, FAQs, shipping and SEO.
 */
export function ProductEditor({ source, initial, onSave, saveLabel, actions }: ProductEditorProps) {
  const { t } = useTranslation("products");
  const richText = useRichTextLabels();
  const rules = useRules();
  const slugRule = useSlugField()(SLUG_MAX);
  const [formError, setFormError] = useState<string | null>(null);
  const [translationErrs, setTranslationErrs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const form = useForm<EditorValues>({
    defaultValues: initial ? toValues(initial) : emptyValues(),
  });
  const category = useWatch({ control: form.control, name: "category" });
  const isHazmat = useWatch({ control: form.control, name: "is_hazmat" });

  const categories = useQuery({
    queryKey: [source.key, "categories"],
    queryFn: source.categories,
    staleTime: 60_000,
  });
  const brands = useQuery({
    queryKey: [source.key, "brands"],
    queryFn: source.brands,
    staleTime: 60_000,
  });
  const taxClasses = useQuery({
    queryKey: [source.key, "tax-classes"],
    queryFn: source.taxClasses,
    staleTime: 60_000,
  });
  const formSchema = useQuery({
    queryKey: [source.key, "form-schema", category],
    queryFn: () => source.formSchema(category),
    enabled: Boolean(category),
  });
  const fields = (formSchema.data?.groups ?? []).flatMap((group) => group.fields);
  const axes = fields.filter((field) => field.is_variant_axis);
  const specCodes = fields.filter((field) => !field.is_variant_axis).map((field) => field.code);
  const errors = form.formState.errors;

  const submit = form.handleSubmit(
    async (values) => {
      setFormError(null);
      setTranslationErrs({});
      setSaving(true);
      try {
        await onSave(toPayload(values, specCodes));
        form.reset(values, { keepValues: true });
      } catch (error) {
        // translation errors are shown in the Translations section (not form fields)
        setTranslationErrs(translationErrors(isApiError(error) ? error.errors : undefined));
        setFormError(applyServerErrorPaths(error, form.setError) ?? t("messages.fixErrors"));
        toast.error(t("messages.fixErrors"));
      } finally {
        setSaving(false);
      }
    },
    () => toast.error(t("messages.fixErrors")),
  );

  return (
    <FormProvider {...form}>
      <form
        noValidate
        onSubmit={(event) => {
          form.clearErrors(); // a server error on a path that isn't a field must not block the next save
          void submit(event);
        }}
        className="space-y-6"
      >
        {formError && <Alert tone="danger">{formError}</Alert>}
        <FormSection
          title={t("editor.sections.basic")}
          description={t("editor.sections.basicHint")}
        >
          <FormField label={t("editor.fields.title")} error={errors.title?.message} required>
            <Input
              {...form.register("title", {
                validate: (value) => value.trim().length > 0 || t("editor.required.title"),
              })}
            />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label={t("editor.fields.category")}
              error={errors.category?.message}
              required
            >
              <Controller
                control={form.control}
                name="category"
                rules={{ required: t("editor.fields.chooseCategory") }}
                render={({ field }) => (
                  <Select
                    value={field.value || NONE}
                    placeholder={t("editor.fields.chooseCategory")}
                    onValueChange={(value) => field.onChange(value === NONE ? "" : value)}
                    options={[
                      { value: NONE, label: t("editor.fields.chooseCategory") },
                      ...flattenCategories(categories.data ?? []),
                    ]}
                  />
                )}
              />
            </FormField>
            <FormField label={t("editor.fields.brand")} error={errors.brand?.message}>
              <Controller
                control={form.control}
                name="brand"
                render={({ field }) => (
                  <Select
                    value={field.value || NONE}
                    onValueChange={(value) => field.onChange(value === NONE ? "" : value)}
                    options={[
                      { value: NONE, label: t("editor.fields.noBrand") },
                      ...(brands.data ?? []).map((brand) => ({
                        value: brand.uid,
                        label: brand.name,
                      })),
                    ]}
                  />
                )}
              />
            </FormField>
            <FormField label={t("editor.fields.condition")} error={errors.condition?.message}>
              <Controller
                control={form.control}
                name="condition"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    options={CONDITIONS.map((value) => ({
                      value,
                      label: t(`editor.condition.${value}`),
                    }))}
                  />
                )}
              />
            </FormField>
            <FormField label={t("editor.fields.mpn")} error={errors.mpn?.message}>
              <Input {...form.register("mpn")} />
            </FormField>
            <FormField label={t("editor.fields.oem")} error={errors.oem_number?.message}>
              <Input {...form.register("oem_number")} />
            </FormField>
          </div>
          <FormField
            label={t("editor.fields.shortDescription")}
            error={errors.short_description?.message}
          >
            <Textarea rows={2} {...form.register("short_description")} />
          </FormField>
          <FormField label={t("editor.fields.description")} error={errors.description?.message}>
            <Controller
              control={form.control}
              name="description"
              render={({ field }) => (
                <RichTextEditor
                  aria-label={t("editor.fields.description")}
                  value={field.value}
                  onChange={field.onChange}
                  labels={richText}
                  invalid={Boolean(errors.description)}
                />
              )}
            />
          </FormField>
        </FormSection>
        <FormSection
          title={t("editor.sections.specs")}
          description={t("editor.sections.specsHint")}
        >
          {!category ? (
            <p className="text-sm text-muted-foreground">{t("editor.pickCategoryFirst")}</p>
          ) : formSchema.isPending ? (
            <LoadingState />
          ) : (
            <SpecsSection groups={formSchema.data?.groups ?? []} />
          )}
        </FormSection>
        <FormSection
          title={t("editor.sections.variants")}
          description={t("editor.sections.variantsHint")}
        >
          {category && formSchema.isPending ? (
            <LoadingState />
          ) : (
            <VariantsSection key={category} axes={axes} />
          )}
        </FormSection>
        <FormSection
          title={t("editor.sections.photos")}
          description={t("editor.sections.photosHint")}
        >
          <PhotosSection />
        </FormSection>
        <FormSection
          title={t("editor.sections.fitment")}
          description={t("editor.sections.fitmentHint")}
        >
          <FitmentSection />
        </FormSection>
        <FormSection title={t("editor.sections.faqs")} description={t("editor.sections.faqsHint")}>
          <FaqsSection />
        </FormSection>
        <FormSection title={t("editor.sections.shipping")}>
          <div className="grid gap-4 sm:grid-cols-4">
            {(
              [
                ["package_weight_g", "weight", "g"],
                ["package_length_mm", "length", "mm"],
                ["package_width_mm", "width", "mm"],
                ["package_height_mm", "height", "mm"],
              ] as const
            ).map(([name, label, unit]) => (
              <FormField
                key={name}
                label={t(`editor.fields.${label}`)}
                error={errors[name]?.message}
              >
                <NumberInput
                  {...form.register(name, {
                    validate: rules.check.whole({ max: MAX_DIMENSION }),
                  })}
                  unit={unit}
                  min={0}
                  max={MAX_DIMENSION}
                  aria-invalid={Boolean(errors[name])}
                />
              </FormField>
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField label={t("editor.fields.warranty")} error={errors.warranty_months?.message}>
              <NumberInput
                {...form.register("warranty_months", {
                  validate: rules.check.whole({ max: MAX_WARRANTY_MONTHS }),
                })}
                min={0}
                max={MAX_WARRANTY_MONTHS}
                aria-invalid={Boolean(errors.warranty_months)}
              />
            </FormField>
            <FormField label={t("editor.fields.returnable")} error={errors.is_returnable?.message}>
              <Controller
                control={form.control}
                name="is_returnable"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    options={(["default", "yes", "no"] as const).map((value) => ({
                      value,
                      label: t(`editor.returnable.${value}`),
                    }))}
                  />
                )}
              />
            </FormField>
            <FormField label={t("editor.fields.taxClass")} error={errors.tax_class?.message}>
              <Controller
                control={form.control}
                name="tax_class"
                render={({ field }) => (
                  <Select
                    value={field.value || NONE}
                    onValueChange={(value) => field.onChange(value === NONE ? "" : value)}
                    options={[
                      { value: NONE, label: t("editor.taxDefault") },
                      ...(taxClasses.data ?? []).map((tax) => ({
                        value: tax.uid,
                        label: tax.name,
                      })),
                    ]}
                  />
                )}
              />
            </FormField>
          </div>
          <FormField label={t("editor.fields.hazmat")}>
            <Controller
              control={form.control}
              name="is_hazmat"
              render={({ field }) => (
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </FormField>
          {isHazmat && (
            <FormField
              label={t("editor.fields.hazmatInfo")}
              error={errors.hazmat_info?.message}
              required
            >
              <Input {...form.register("hazmat_info")} />
            </FormField>
          )}
        </FormSection>
        <TranslationsField
          control={form.control}
          errors={translationErrs}
          fields={[
            { name: "title", label: t("editor.fields.title"), maxLength: 200 },
            {
              name: "short_description",
              label: t("editor.fields.shortDescription"),
              kind: "textarea",
              maxLength: 500,
            },
            { name: "description", label: t("editor.fields.description"), kind: "html" },
            { name: "seo_title", label: t("editor.fields.seoTitle"), maxLength: 160 },
            {
              name: "seo_description",
              label: t("editor.fields.seoDescription"),
              kind: "textarea",
              maxLength: 320,
            },
          ]}
        />
        <FormSection title={t("editor.sections.seo")}>
          <FormField
            label={t("editor.fields.slug")}
            hint={t("editor.fields.slugHint")}
            error={errors.slug?.message}
          >
            <PatternInput
              {...textRules.slug}
              maxLength={SLUG_MAX}
              aria-invalid={Boolean(errors.slug)}
              {...form.register("slug", {
                validate: (value) => {
                  const result = slugRule.safeParse(value);
                  return result.success || (result.error.issues[0]?.message ?? false);
                },
              })}
            />
          </FormField>
          <FormField label={t("editor.fields.seoTitle")} error={errors.seo_title?.message}>
            <Input {...form.register("seo_title")} />
          </FormField>
          <FormField
            label={t("editor.fields.seoDescription")}
            error={errors.seo_description?.message}
          >
            <Textarea rows={2} {...form.register("seo_description")} />
          </FormField>
          <FormField label={t("editor.fields.published")}>
            <Controller
              control={form.control}
              name="is_published"
              render={({ field }) => (
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </FormField>
        </FormSection>

        <FormActions className="sticky bottom-0 z-10 border-t border-border bg-background py-3">
          {actions}
          <Button type="submit" loading={saving}>
            {saveLabel}
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  );
}
